import { Component } from '@theme/component';

/**
 * @typedef {object} BroskePhotoReviewsRefs
 * @property {HTMLUListElement} [track]
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 */

/**
 * Photo reviews carousel for `sections/broske-photo-reviews.liquid`.
 * The track scrolls natively (swipe / trackpad); the arrows page it by one visible width.
 *
 * @extends {Component<BroskePhotoReviewsRefs>}
 */
class BroskePhotoReviewsComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  previous() {
    this.#page(-1);
  }

  next() {
    this.#page(1);
  }

  #listen() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.track?.addEventListener('scroll', this.#updateArrows, { passive: true, signal });
    window.addEventListener('resize', this.#updateArrows, { passive: true, signal });
    this.#updateArrows();
  }

  /** @param {number} direction */
  #page(direction) {
    const track = this.refs.track;
    if (!track) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({
      left: track.clientWidth * 0.9 * direction,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #updateArrows = () => {
    const { track, prev, next } = this.refs;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    if (prev) prev.disabled = position <= 2;
    if (next) next.disabled = position >= maxScroll - 2;
    this.toggleAttribute('data-scrollable', maxScroll > 2);
  };
}

if (!customElements.get('broske-photo-reviews-component')) {
  customElements.define('broske-photo-reviews-component', BroskePhotoReviewsComponent);
}
